import { t } from 'i18next';
import { isNil } from 'lodash';
import { Controller, Form, FormProvider } from 'react-hook-form';
import {
  Box,
  Radio,
  Button,
  RadioGroup,
  Typography,
  FormControl,
  FormHelperText,
  FormControlLabel,
} from '@mui/material';

import { CustomModal } from '@components';
import { theme } from '@store';

import { useAssignModal } from './hooks/useAssignModal';
import { UserSearchFormField } from 'components/DynamicFormFields';
import { Select } from 'ui';

interface IAssignModalProps {
  task_id: string;
  disabled: boolean;
}

export const AssignModal = ({ task_id, disabled }: IAssignModalProps) => {
  const {
    formMethods,
    onSubmit,
    loading,
    isOpen,
    handleOpen,
    handleClose,
    teamsShortOptions,
    assignTypeOptions,
  } = useAssignModal(task_id);

  const {
    control,
    formState: { isSubmitting },
    watch,
  } = formMethods;

  const type = watch('type');

  return (
    <>
      <Button
        sx={{
          fontWeight: 'regular',
          fontSize: '1rem',
          width: '100%',
        }}
        variant='outlined'
        onClick={handleOpen}
        disabled={disabled}
      >
        {t('worklist.worklistItem.assignModal.openButtonText')}
      </Button>
      <CustomModal
        isOpen={isOpen}
        onClose={handleClose}
        isLoading={loading}
        title={t('worklist.worklistItem.assignModal.title')}
      >
        <FormProvider {...formMethods}>
          <Form
            control={control}
            onSubmit={e => onSubmit(e.event)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <Box>
              <Typography variant='body1' component='h2'>
                {t(
                  'worklist.worklistItem.assignModal.typeRadioGroupDescription',
                )}
              </Typography>
              <Controller
                name={'type'}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <FormControl>
                    <RadioGroup
                      sx={{
                        ml: '1rem',
                      }}
                      row
                      {...field}
                      defaultValue={assignTypeOptions[0]}
                      onChange={(_e, val) => field.onChange(val)}
                    >
                      {assignTypeOptions.map(option => (
                        <FormControlLabel
                          key={option.value}
                          value={option.value}
                          control={<Radio />}
                          label={option.label}
                        />
                      ))}
                    </RadioGroup>
                    <FormHelperText error={!isNil(error)}>
                      {error ? error.message : null}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Box>

            {type === 'team' && (
              <Controller
                name='team_id'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Select
                    {...field}
                    label={t(
                      'worklist.worklistItem.assignModal.teamSelectLabel',
                    )}
                    error={!isNil(error)}
                    options={teamsShortOptions}
                    helperText={error?.message}
                    required
                  />
                )}
              />
            )}

            {type === 'user' && <UserSearchFormField name='user_id' />}

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
                position: 'sticky',
                bottom: 0,
                paddingY: 3,
                backgroundColor: theme.palette.common.white,
                zIndex: 3,
              }}
            >
              <Button
                variant='text'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                }}
                disabled={isSubmitting}
                onClick={handleClose}
              >
                {t('general.cancel')}
              </Button>
              <Button
                type='submit'
                variant='contained'
                color='secondary'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                  color: 'white',
                }}
                loading={loading}
              >
                {t('worklist.worklistItem.assignModal.openButtonText')}
              </Button>
            </Box>
          </Form>
        </FormProvider>
      </CustomModal>
    </>
  );
};
